/**
 * UserActorEnvelope v2 — the `x-user-context` metadata envelope carried
 * across the S2S boundary per D454 + `01-communication-doctrine § 6`.
 *
 * grpc-auth is the FOUNDATION lib (both `@nodii/auth-sdk` and
 * `@nodii/grpc-interceptors` depend on it), so the canonical envelope
 * type + wire codec live HERE. The edge stamps this exact wire shape and
 * `@nodii/grpc-interceptors` `enrichAuthContext` reads it.
 *
 * WIRE SHAPE = snake_case JSON, base64-encoded, in the `x-user-context`
 * gRPC metadata header — byte-identical to the edge producer
 * (`buildUserActorEnvelope`) + the `@nodii/auth-sdk` codec. (Prior to
 * D454 this type was camelCase — a stale divergence from the actual wire
 * that left `enrichAuthContext`'s snake_case `tenant_id` read blind;
 * reconciled here.)
 *
 * DIGEST-ONLY (D425 §5.4): the envelope carries `permissions_digest` (a
 * PROOF, not an authorization grant) — NEVER a literal `permissions[]`.
 * Sanctioned uses: audit attribution, MFA-freshness, RLS-pool / tenant
 * selection, and the confused-deputy cross-tenant fence (D454).
 */
export interface UserActorEnvelope {
  /** Opaque user id (uuid string) — the effective principal. */
  user_id: string;
  /** Tenant id this principal is acting within (`null` = tenant-less). */
  tenant_id: string | null;
  /** Role stamp (D425/D427); one of {@link USER_ROLES}. */
  user_role: UserRole;
  /**
   * sha256 of the principal's permission set — a PROOF used to detect
   * stale-grant races (re-fetch when it differs from the local cache),
   * NEVER an authorization grant (D425 digest-only).
   */
  permissions_digest: string;
  /** Authentication Method References (RFC 8176); MFA-freshness input. */
  amr: string[];
  /** Epoch seconds the user JWT was issued — basis for MFA freshness. */
  iat: number;
  /** jti of the user JWT (NOT the S2S jti) — revocation traceability. */
  user_jwt_jti: string;
  /** Opaque request id that originated the chain (first-hop middleware). */
  source_request_id: string;
  /**
   * D454 agent-actor marker (OPTIONAL — accommodation for the pipeline
   * agent vectors 1 & 2: an agent acting on behalf of a customer / user,
   * inheriting the operator's permissions). Absent for direct human /
   * customer traffic. The effective principal's `tenant_id` /
   * `permissions_digest` above are UNCHANGED (inherited); this only
   * records that an agent is the actual driver, so downstream authz +
   * audit can distinguish "agent acting as X" from "X directly".
   * PRODUCED by nodii-auth-service; libs decode + relay it unchanged.
   */
  on_behalf_of?: OnBehalfOf;
}

/** D454 agent-actor "acting on behalf of" marker. */
export interface OnBehalfOf {
  /** The underlying principal the agent inherits from. */
  principal_kind: "customer" | "user";
  /** The agent's own opaque id (audit attribution). */
  agent_id: string;
}

/**
 * Canonical `UserRole` values (D425/D427). Parity with `@nodii/auth-sdk`
 * `USER_ROLES` + Python / Go. `platform_admin` MUST be accepted (the
 * rewritten s2s admin gate stamps on it).
 */
export type UserRole = "tenant_user" | "tenant_customer" | "platform_admin";

/** All canonical `UserRole` values, for runtime validation. */
export const USER_ROLES: readonly UserRole[] = [
  "tenant_user",
  "tenant_customer",
  "platform_admin",
];

/** Narrowing type-guard for a `user_role` string (D425). */
export function isUserRole(value: string): value is UserRole {
  return (USER_ROLES as readonly string[]).includes(value);
}

/**
 * The gRPC metadata header carrying the base64-encoded `UserActorEnvelope`.
 * Exported so producers + consumers reference ONE canonical header name
 * instead of hand-typing the literal (header-name drift).
 */
export const X_USER_CONTEXT_HEADER = "x-user-context";

/** Thrown when an inbound `x-user-context` header is malformed / invalid. */
export class UserActorEnvelopeError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "UserActorEnvelopeError";
  }
}

/**
 * ACCEPT a decoded v2 `UserActorEnvelope` object (validation + narrowing).
 * Throws {@link UserActorEnvelopeError} for an unknown `user_role`.
 */
export function acceptUserActorEnvelope(raw: {
  user_id: string;
  tenant_id: string | null;
  user_role: string;
  permissions_digest: string;
  amr?: string[];
  iat?: number;
  user_jwt_jti?: string;
  source_request_id?: string;
  on_behalf_of?: OnBehalfOf;
}): UserActorEnvelope {
  if (!isUserRole(raw.user_role)) {
    throw new UserActorEnvelopeError(
      `UserActorEnvelope.user_role must be one of ${USER_ROLES.join(", ")}; got '${raw.user_role}'`,
    );
  }
  const env: UserActorEnvelope = {
    user_id: raw.user_id,
    tenant_id: raw.tenant_id,
    user_role: raw.user_role,
    permissions_digest: raw.permissions_digest,
    amr: raw.amr ?? [],
    iat: raw.iat ?? 0,
    user_jwt_jti: raw.user_jwt_jti ?? "",
    source_request_id: raw.source_request_id ?? "",
  };
  // Relay the D454 agent marker unchanged when present (libs never author
  // it — nodii-auth-service does — but decode/forward must preserve it).
  if (raw.on_behalf_of !== undefined) {
    env.on_behalf_of = raw.on_behalf_of;
  }
  return env;
}

/**
 * Canonical PRODUCER-side encoder: `base64(JSON(envelope))` in FIXED field
 * order for byte-parity with the Python (`encode_user_actor_envelope`) + Go
 * (`EncodeUserActorEnvelope`) encoders + the edge producer. Standard (not
 * URL-safe) base64. `on_behalf_of` is emitted only when present (byte-parity
 * for the common non-agent envelope).
 */
export function encodeUserActorEnvelope(env: UserActorEnvelope): string {
  const ordered: Record<string, unknown> = {
    user_id: env.user_id,
    tenant_id: env.tenant_id,
    user_role: env.user_role,
    permissions_digest: env.permissions_digest,
    amr: env.amr ?? [],
    iat: env.iat,
    user_jwt_jti: env.user_jwt_jti,
    source_request_id: env.source_request_id,
  };
  if (env.on_behalf_of !== undefined) {
    ordered.on_behalf_of = env.on_behalf_of;
  }
  return Buffer.from(JSON.stringify(ordered), "utf8").toString("base64");
}

/**
 * Canonical CONSUMER-side decoder: the inverse of
 * {@link encodeUserActorEnvelope}. base64-decodes + `JSON.parse`s the
 * `x-user-context` header value, then validates via
 * {@link acceptUserActorEnvelope}. Malformed base64 / JSON / non-object /
 * unknown-role all throw {@link UserActorEnvelopeError} — so a consumer's
 * `catch (e) { if (e instanceof UserActorEnvelopeError) ... }` handles
 * adversarial header input uniformly across TS / Python / Go.
 */
export function decodeUserActorEnvelope(
  headerValue: string,
): UserActorEnvelope {
  let raw: unknown;
  try {
    // STRICT standard base64 (charset + length multiple of 4). `Buffer.from`
    // is lenient (strips whitespace, accepts base64url `-_`, missing padding);
    // Go's `base64.StdEncoding` is strict, so without this guard the SAME
    // header could decode on TS but throw on Go — the cross-lang drift this
    // codec exists to remove.
    if (
      headerValue.length % 4 !== 0 ||
      !/^[A-Za-z0-9+/]*={0,2}$/.test(headerValue)
    ) {
      throw new Error("non-canonical standard base64");
    }
    raw = JSON.parse(Buffer.from(headerValue, "base64").toString("utf8"));
  } catch (err) {
    throw new UserActorEnvelopeError(
      `x-user-context: malformed base64/JSON (${String(err)})`,
    );
  }
  if (raw === null || typeof raw !== "object") {
    throw new UserActorEnvelopeError(
      "x-user-context: envelope must be a JSON object",
    );
  }
  return acceptUserActorEnvelope(
    raw as Parameters<typeof acceptUserActorEnvelope>[0],
  );
}
